/**
 * Lightweight "as you type" ingredient parser (Screen E · manual entry: "2 cups flour"
 * splits itself). Purely cosmetic/best-effort — the raw line always wins as `text`, so a
 * wrong guess never blocks saving (DESIGN-BRIEF §5).
 */
const UNIT_WORDS = new Set([
  'cup',
  'cups',
  'tbsp',
  'tablespoon',
  'tablespoons',
  'tsp',
  'teaspoon',
  'teaspoons',
  'lb',
  'lbs',
  'pound',
  'pounds',
  'oz',
  'ounce',
  'ounces',
  'g',
  'gram',
  'grams',
  'kg',
  'ml',
  'l',
  'liter',
  'liters',
  'pint',
  'pints',
  'quart',
  'quarts',
  'gallon',
  'gallons',
  'clove',
  'cloves',
  'can',
  'cans',
  'package',
  'packages',
  'pack',
  'packs',
  'head',
  'heads',
  'bunch',
  'bunches',
  'stick',
  'sticks',
  'slice',
  'slices',
  'pinch',
]);

export interface ParsedIngredientLine {
  text: string;
  quantity?: string;
  unit?: string;
  name?: string;
}

// A single quantity "word": a glued whole+unicode-fraction ("1½"), a plain fraction
// ("1/2"), a decimal/integer ("1.5", "2"), or a bare unicode fraction ("¼"). Order matters
// — the glued and fraction forms must be tried before the plain decimal so "1½" isn't
// mis-split into "1" + a dangling "½" (DECISIONS 2026-08-01: mixed numbers like "1 1/2 cups
// milk" were silently landing in `name` instead of `quantity`/`unit`, which broke scaling).
const FRACTION_CHARS = '¼½¾⅓⅔⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞';
const NUMBER_TOKEN = `(?:\\d+[${FRACTION_CHARS}]|\\d+\\/\\d+|\\d+(?:\\.\\d+)?|[${FRACTION_CHARS}])`;
// A quantity is one token, optionally a "whole fraction" pair ("1 1/2"), optionally a range
// ("2-3", "1 1/2 - 2 1/2").
const QUANTITY_RE = new RegExp(
  `^(${NUMBER_TOKEN}(?:\\s+${NUMBER_TOKEN})?(?:\\s*-\\s*${NUMBER_TOKEN}(?:\\s+${NUMBER_TOKEN})?)?)\\s+(.*)$`,
);

/** Best-effort split of a free-text ingredient line into quantity/unit/name. */
export function parseIngredientLine(raw: string): ParsedIngredientLine {
  const text = raw.trim();
  if (!text) return { text };

  const qtyMatch = QUANTITY_RE.exec(text);
  if (!qtyMatch) return { text };

  const [, quantity, rest] = qtyMatch as unknown as [string, string, string];
  const words = rest.split(/\s+/);
  const first = words[0]?.toLowerCase().replace(/[.,]$/, '');
  if (first && UNIT_WORDS.has(first)) {
    return { text, quantity, unit: words[0], name: words.slice(1).join(' ') || undefined };
  }
  return { text, quantity, name: rest || undefined };
}
